import { Filter } from '@bitrise/bitkit';
import { DateTime } from 'luxon';
import BuildListFilters from '@/pages/App/AppDetails/types/BuildListFilters';
import { segmentTrack } from '@/hooks/analytics/useTracking';
import useBuildFilterData from '../hooks/useBuildFilterData';
import useBuildFilterState, { BuildFilterName, BuildFilterState, allBuildFilters } from '../hooks/useBuildFilterState';
import useAppBranchesForFilter from '../../hooks/useAppBranchesForFilter';
import useAppWorkflowsForFilter from '../../hooks/useAppWorkflowsForFilter';

type BuildFiltersProps = {
  appSlug: string;
  setFilters(filters: BuildListFilters): void;
};

const BuildFilters = ({ appSlug, setFilters: updateFilters }: BuildFiltersProps) => {
  const { data: workflowsAndPipelines, fetchQuery: fetchPipelineables } = useAppWorkflowsForFilter({
    appSlug,
    withPipelineWorkflows: false,
  });

  const { data: branches, fetchQuery: fetchBranches } = useAppBranchesForFilter({ appSlug });

  const filterData = useBuildFilterData({
    branches,
    fetchBranches,
    fetchPipelineables,
    pipelines: workflowsAndPipelines?.pipelines,
    workflows: workflowsAndPipelines?.workflows,
  });

  const { state, setFilters } = useBuildFilterState(updateFilters);

  const onChange = (newState: BuildFilterState) => {
    const filters: { key: BuildFilterName; val: string }[] = allBuildFilters
      .filter((key) => !['after_date', 'before_date'].includes(key))
      .map((key) => {
        const newVal = newState[key as keyof BuildFilterState]?.[0];
        return {
          key: key as BuildFilterName,
          val: newVal === 'all' ? '' : newVal,
        };
      });

    if (newState.date_range) {
      filters.push({
        key: 'after_date',
        val: newState.date_range[0],
      });

      filters.push({
        key: 'before_date',
        val: newState.date_range[1],
      });
    }

    const options: string[] = [];
    const values: string[] = [];

    Object.keys(newState)
      .filter((key) => newState[key as keyof BuildFilterState][0] !== 'all')
      .forEach((key) => {
        options.push(key);
        values.push(newState[key as keyof BuildFilterState][0]);
      });

    if (options.length || newState.date_range) {
      segmentTrack('Build List Filter Changed', {
        filter_options: options.join(', '),
        filter_values: values.join(', '),
        filter_date_period_start: newState.date_range && DateTime.fromMillis(Number(newState.date_range[0])).toISO(),
        filter_date_period_end: newState.date_range && DateTime.fromMillis(Number(newState.date_range[1])).toISO(),
        app_slug: appSlug,
      });
    }

    setFilters(filters);
  };
  return (
    <Filter
      data-testid="build-filters"
      defaultState={{ date_range: ['', ''] }}
      paddingTop={16}
      onChange={(newState) => onChange(newState as BuildFilterState)}
      state={state as BuildFilterState}
      data={filterData}
      showAdd={false}
      showSearch
      searchTooltip="Build number or commit message"
    />
  );
};

export default BuildFilters;
